import type { Question } from "../types/index.js";

/**
 * FNV-1a hash producing a stable 64-bit seed from a string.
 * Uses BigInt because JS numbers lose precision beyond 2^53.
 */
function fnv1aHash(text: string): bigint {
  let hash = 14695981039346656037n; // FNV offset basis (same as Swift 1469598103934665603 but full 64-bit)
  const encoder = new TextEncoder();
  const bytes = encoder.encode(text);
  for (const byte of bytes) {
    hash ^= BigInt(byte);
    hash = BigInt.asUintN(64, hash * 1099511628211n);
  }
  return hash;
}

/**
 * Simple LCG PRNG matching the Swift SeededGenerator.
 */
class SeededGenerator {
  private state: bigint;

  constructor(seed: bigint) {
    this.state = seed === 0n ? 0x9E3779B97F4A7C15n : seed;
  }

  next(): bigint {
    this.state = BigInt.asUintN(
      64,
      this.state * 6364136223846793005n + 1442695040888963407n,
    );
    return this.state;
  }
}

/**
 * Deterministically shuffle choices for a question using FNV-1a seed.
 * Matches the Swift implementation exactly.
 */
export function shuffleChoices(q: Question): Question {
  if (q.choices.length <= 1) return q;

  const text = `${q.id}|${q.question}|${q.choices.join("|")}`;
  const seed = fnv1aHash(text);
  const gen = new SeededGenerator(seed);

  const indices = q.choices.map((_, i) => i);
  // Fisher-Yates using the seeded generator (same order as Swift's .shuffle(using:))
  for (let i = indices.length - 1; i > 0; i--) {
    const rand = gen.next();
    const j = Number(rand % BigInt(i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }

  const newAnswer = indices.indexOf(q.answer);
  if (newAnswer === -1) return q;

  return {
    ...q,
    choices: indices.map((i) => q.choices[i]),
    answer: newAnswer,
  };
}
